import { useCallback, useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';

import { useAppContext } from '../../contextApi/context';
import { getMovementDetails } from '../../services/StockMovementService';
import Spinner from './Spinner';
import { formatDate, formatDateAndTime } from '../../utils/dates';
import { formatCurrency } from '../../utils/numbers';

const StockMovementDetailsModal = ({ closeHandler, movementId, show }) => {
    // ----- Context
    const { loggedUser } = useAppContext();

    // ----- State
    let [movementDetails, setMovementDetails] = useState(null);
    let [returnMovementDetails, setReturnMovementDetails] = useState(null);

    // ----- API Fetch Actions
    const fetchStockMovementDetails = useCallback(
        async (id) => {
            return await getMovementDetails(loggedUser.token, id);
        },
        [loggedUser.token]
    );

    // ----- Hooks
    useEffect(() => {
        fetchStockMovementDetails(movementId).then((movementData) => {
            // console.log('Response MODAL: ', movementData);

            // Getting return/annulment data when needed
            if (movementData.returnMovement !== null) {
                fetchStockMovementDetails(movementData.returnMovement).then((returnData) => {
                    setMovementDetails(movementData);
                    setReturnMovementDetails(returnData);
                });
            } else {
                setMovementDetails(movementData);
            }
        });
    }, [fetchStockMovementDetails, movementId]);

    // ----- Actions
    const handleClose = () => {
        setMovementDetails(null);
        closeHandler();
    };

    // ----- Render
    return (
        <Modal show={show} size='lg' backdrop='static' centered onHide={handleClose}>
            <Modal.Header className='fw-bold' closeButton>
                Detalles de movimiento
            </Modal.Header>
            <Modal.Body>
                {movementDetails ? (
                    <>
                        <div className='d-flex align-items-center mb-3 justify-content-between'>
                            <div className='d-flex'>
                                <label className='fw-bold me-3'>Fecha de registro:</label>
                                <p className='m-0'>{formatDateAndTime(movementDetails.createdDate)}</p>
                            </div>
                            {movementDetails.type === 'RECEIPT' && returnMovementDetails?.annulmentType === 'COMPLETE' && (
                                <span class='badge rounded-pill bg-danger'>FACTURA ANULADA</span>
                            )}
                        </div>
                        <div className='d-flex'>
                            <label className='fw-bold me-3'>Hecho por:</label>
                            <p>{`${movementDetails.user?.firstName} ${movementDetails.user?.lastName} (${movementDetails.user?.username})`}</p>
                        </div>

                        {movementDetails.type === 'RECEIPT' && (
                            <>
                                {returnMovementDetails?.annulmentType === 'COMPLETE' && (
                                    <div className='d-flex'>
                                        <label className='fw-bold me-3'>Anulado por:</label>
                                        <p>{`${returnMovementDetails?.user?.firstName} ${returnMovementDetails?.user?.lastName} (${returnMovementDetails?.user?.username})`}</p>
                                    </div>
                                )}
                                <div className='d-flex'>
                                    <label className='fw-bold me-3'>Factura:</label>
                                    <p>
                                        {movementDetails.invoice} {movementDetails.eventDate && `(${formatDate(movementDetails.eventDate)})`}
                                    </p>
                                </div>
                                <div className='d-flex'>
                                    <label className='fw-bold me-3'>Moneda:</label>
                                    <p>{movementDetails.currency === 'CRC' ? 'Colón (₡)' : 'Dólar ($)'}</p>
                                </div>
                                {movementDetails.currency === 'USD' && (
                                    <div className='d-flex'>
                                        <label className='fw-bold me-3'>Tipo de cambio:</label>
                                        <p>{`₡${formatCurrency(movementDetails.referenceValue)}`}</p>
                                    </div>
                                )}
                                <div className='d-flex'>
                                    <label className='fw-bold me-3'>Proveedor:</label>
                                    <p>{movementDetails.details[0]?.providerName}</p>
                                </div>
                            </>
                        )}

                        {movementDetails.type === 'ISSUE' && (
                            <>
                                <div className='d-flex'>
                                    <label className='fw-bold me-3'>Fecha de despacho:</label>
                                    <p>{movementDetails.eventDate ? formatDateAndTime(movementDetails.eventDate) : '-'}</p>
                                </div>
                                <div className='d-flex'>
                                    <label className='fw-bold me-3'>Tipo de despacho:</label>
                                    <p>{movementDetails.issueType ? movementDetails.issueType : '-'}</p>
                                </div>
                                {movementDetails.destination && (
                                    <div className='d-flex'>
                                        <label className='fw-bold me-3'>Vehículo:</label>
                                        <p>{`${movementDetails.destination.make} - ${movementDetails.destination.model} (${movementDetails.destination.plate})`}</p>
                                    </div>
                                )}
                                <div className='d-flex'>
                                    <label className='fw-bold me-3'>Recibido por:</label>
                                    <p>{`${movementDetails.receivingUser?.firstName} ${movementDetails.receivingUser?.lastName} (${movementDetails.receivingUser?.username})`}</p>
                                </div>
                            </>
                        )}

                        {movementDetails.description && (
                            <div className='d-flex'>
                                <label className='fw-bold me-3'>Descripción:</label>
                                <p>{movementDetails.description}</p>
                            </div>
                        )}

                        <hr />

                        <div className='d-flex flex-column'>
                            <label className='fw-bold'>
                                {movementDetails.type === 'ISSUE'
                                    ? 'Detalles de la salida'
                                    : movementDetails.type === 'RECEIPT'
                                    ? 'Detalles de la compra'
                                    : 'Detalles'}
                                :
                            </label>
                            <table className='table table-bordered my-2'>
                                <thead>
                                    <tr>
                                        <th scope='col'>{movementDetails.type === 'RECEIPT' ? 'Concepto' : 'Artículo'}</th>
                                        <th scope='col'>Marca</th>
                                        <th scope='col'>Cantidad</th>
                                        <th scope='col'>Precio unitario</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {movementDetails.details?.map((item, index) => (
                                        <tr key={index}>
                                            <td>{item.isService ? item.serviceConcept : item.articleName}</td>
                                            <td>{item.isService ? '--' : item.brand}</td>
                                            <td>{item.amount}</td>
                                            <td>{`₡${formatCurrency(item.itemsValue)}`}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className='d-flex'>
                            <label className='fw-bold me-3'>
                                {movementDetails.type === 'ISSUE'
                                    ? 'Valor total de la salida'
                                    : movementDetails.type === 'RECEIPT'
                                    ? 'Valor total de la compra'
                                    : 'Valor total'}
                            </label>
                            <p>{`₡${formatCurrency(movementDetails.totalValue)}`}</p>
                        </div>

                        {returnMovementDetails !== null && (
                            <>
                                <hr />

                                <div className='d-flex flex-column'>
                                    <label className='fw-bold'>
                                        {movementDetails.type === 'ISSUE' ? 'Detalles de la devolución' : 'Detalles de la anulación'}:
                                    </label>
                                    <table className='table table-bordered my-2'>
                                        <thead>
                                            <tr>
                                                <th scope='col'>{movementDetails.type === 'RECEIPT' ? 'Concepto' : 'Artículo'}</th>
                                                <th scope='col'>Marca</th>
                                                <th scope='col'>Cantidad</th>
                                                <th scope='col'>Precio unitario</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {returnMovementDetails?.details?.map((item, index) => (
                                                <tr key={index}>
                                                    <td>{item.isService ? item.serviceConcept : item.articleName}</td>
                                                    <td>{item.isService ? '--' : item.brand}</td>
                                                    <td>{item.amount}</td>
                                                    <td>{`₡${formatCurrency(item.itemsValue)}`}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className='d-flex'>
                                    <label className='fw-bold me-3'>
                                        {movementDetails.type === 'ISSUE' ? 'Valor total de la devolución' : 'Valor total de la anulación'}:
                                    </label>
                                    <p>{`₡${formatCurrency(returnMovementDetails?.totalValue)}`}</p>
                                </div>

                                <hr />

                                <div className='d-flex justify-content-center'>
                                    <label className='fw-bold me-3'>Valor total:</label>
                                    <p>{`₡${formatCurrency(movementDetails.totalValue - returnMovementDetails.totalValue)}`}</p>
                                </div>
                            </>
                        )}
                    </>
                ) : (
                    <Spinner />
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button onClick={handleClose}>Cerrar</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default StockMovementDetailsModal;
